import { ChevronUp, ChevronDown } from 'lucide-react';

function StepField({ field, value, onChange, testid }) {
  const i = field.options.indexOf(value);
  const numeric = field.options.every((v) => /^\d+$/.test(v));
  const move = (dir) => onChange(field.options[i < 0 ? 0 : Math.max(0, Math.min(field.options.length - 1, i + dir))]);
  const invalid = value && i < 0;
  return (
    <div className="min-w-0">
      <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 truncate">{field.label}</label>
      <div className={`flex items-stretch rounded-md border bg-white dark:bg-slate-900 ${invalid ? 'border-red-400' : 'border-slate-300 dark:border-slate-600'}`}>
        <input
          role="spinbutton" aria-label={field.label} aria-invalid={!!invalid} inputMode={numeric ? 'numeric' : 'text'} autoComplete="off" value={value}
          aria-valuemin={numeric ? Number(field.options[0]) : 0} aria-valuemax={numeric ? Number(field.options.at(-1)) : field.options.length - 1}
          aria-valuenow={i < 0 ? undefined : numeric ? Number(value) : i} aria-valuetext={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onBlur={() => { if (numeric && value && field.options.includes(String(Number(value)))) onChange(String(Number(value))); }}
          onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); move(e.key === 'ArrowUp' ? 1 : -1); } }}
          className="w-full min-w-0 h-8 px-2 text-sm font-mono bg-transparent outline-none"
          title={`Enter an available ${field.label.toLowerCase()} (${field.options[0]}–${field.options.at(-1)}) or use the arrows.`}
          data-testid={`${testid}-${field.label.toLowerCase().replace(/\s+/g, '-')}`}
        />
        <div className="flex flex-col border-l border-slate-200">
          <button type="button" aria-label={`Increase ${field.label}`} data-testid={`${testid}-${field.label.toLowerCase().replace(/\s+/g, '-')}-increase`} disabled={i === field.options.length - 1} onClick={() => move(1)} className="flex-1 px-1 text-slate-500 hover:text-slate-900 disabled:opacity-30"><ChevronUp size={12} /></button>
          <button type="button" aria-label={`Decrease ${field.label}`} data-testid={`${testid}-${field.label.toLowerCase().replace(/\s+/g, '-')}-decrease`} disabled={i <= 0} onClick={() => move(-1)} className="flex-1 px-1 text-slate-500 hover:text-slate-900 disabled:opacity-30 border-t border-slate-200"><ChevronDown size={12} /></button>
        </div>
      </div>
    </div>
  );
}

// Structured LSD entry (CSM lsd-bar): one stepper per component, joined with '-' into the LSD string.
export default function LsdBar({ layout, values = {}, onChange, testid = 'lsd-bar' }) {
  if (!layout) return null;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-[repeat(auto-fit,minmax(76px,1fr))] gap-1.5 min-w-0" data-testid={testid}>
      {layout.fields.map((f) => (
        <StepField key={f.label} field={f} value={values[f.label] || ''} onChange={(v) => onChange({ ...values, [f.label]: v })} testid={testid} />
      ))}
    </div>
  );
}


// AB/SK → 04-12-048-07W5, BC NTS → a-12-B/94-H-2; partial entries fall back to a plain join.
export const lsdFromComponents = (layout, v) => {
  if (!layout) return '';
  if (!Object.values(v).some(Boolean)) return '';
  if (!lsdComplete(layout, v)) return layout.fields.map((f) => v[f.label] || '').join('-');
  const pad = (s, n) => String(Number(s)).padStart(n, '0');
  if (layout.name === 'ALBERTA & SASK LSD') return `${pad(v.LSD, 2)}-${pad(v.SECTION, 2)}-${pad(v.TOWNSHIP, 3)}-${pad(v.RANGE, 2)}W${Number(v.MERIDIAN)}`;
  if (layout.name === 'BC LSD') return `${v.QUARTER.toLowerCase()}-${v.CENTRIZONE}-${v['ZONE BLOCK']}/${v['MAP UNIT']}-${v['MAP UNIT SUB']}-${v['MAP SHEET']}`;
  return layout.fields.map((f) => v[f.label]).join('-');
};
export const lsdComplete = (layout, values) => !!layout && layout.fields.every((f) => f.options.includes(values[f.label]));
